import { catalogById, plants } from "../data/catalog";
import type { BalconyProfile, DesignItem } from "../data/model";
import { servicePackages } from "../data/services";

export interface EstimateLine {
  refId: string;
  name: string;
  type: DesignItem["type"];
  qty: number;
  unitPrice: number;
  total: number;
}

export const areaM2 = (widthCm: number, depthCm: number) =>
  Math.round(((widthCm * depthCm) / 10000) * 100) / 100;

export const estimateLines = (design: DesignItem[]): EstimateLine[] => {
  const aggregated = new Map<string, EstimateLine>();
  design.forEach((line) => {
    const record = catalogById.get(line.refId);
    if (!record) return;
    const existing = aggregated.get(record.id);
    const qty = (existing?.qty ?? 0) + line.qty;
    aggregated.set(record.id, {
      refId: record.id,
      name: record.name,
      type: record.type,
      qty,
      unitPrice: record.unitPrice,
      total: record.unitPrice * qty,
    });
  });
  return Array.from(aggregated.values());
};

export const materialSubtotal = (design: DesignItem[]) =>
  estimateLines(design).reduce((sum, line) => sum + line.total, 0);

export const estimateTotal = (design: DesignItem[], packageId: string) => {
  const service = servicePackages.find((entry) => entry.id === packageId);
  return materialSubtotal(design) + (service?.price ?? 0);
};

export const plantCount = (design: DesignItem[]) =>
  design.filter((item) => item.type === "plant").reduce((sum, item) => sum + item.qty, 0);

export const careMinutes = (design: DesignItem[]) =>
  design.reduce((sum, item) => {
    if (item.type !== "plant") return sum;
    const plant = plants.find((entry) => entry.id === item.refId);
    return sum + (plant?.careMinutesPerWeek ?? 0) * item.qty;
  }, 0);

export const wateringSessions = (design: DesignItem[]) =>
  design.reduce((max, item) => {
    if (item.type !== "plant") return max;
    const plant = plants.find((entry) => entry.id === item.refId);
    return Math.max(max, plant?.wateringPerWeek ?? 0);
  }, 0);

export const aisleWidthCm = (profile: BalconyProfile, design: DesignItem[]) => {
  const backItems = design
    .map((item) => catalogById.get(item.refId))
    .filter((item) => item && item.type !== "accessory");
  const maxDepth = Math.max(0, ...backItems.map((item) => item?.depthCm ?? 0));
  return Math.max(35, profile.depthCm - Math.min(maxDepth, 85));
};

export const footprintRatio = (profile: BalconyProfile, design: DesignItem[]) => {
  const footprint = design.reduce((sum, item) => {
    const record = catalogById.get(item.refId);
    if (!record || record.type === "accessory") return sum;
    return sum + record.widthCm * record.depthCm * item.qty;
  }, 0);
  return footprint / (profile.widthCm * profile.depthCm);
};
